/* eslint-disable no-console */
import { ReactNode } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { message } from "antd";
import { ConfigOnClose } from "antd/lib/message";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";

export type LoggerFn = (message: string, extraData?: Record<string, any>) => void;
type ErrorFn = (message: string | Error, extraData?: Record<string, any>) => void;

export const LOG_LEVELS = {
    debug: "debug",
    log: "log",
    warn: "warn",
    error: "error",
    action: "action",
} as const;

export type LogLevel = (typeof LOG_LEVELS)[keyof typeof LOG_LEVELS];
export type ErrorLogger = {
    error: ErrorFn;
    warn: LoggerFn & ErrorFn;
};

export function getLogger(
    context: string,
    metadata: Record<string, any> = {}
): Record<LogLevel, LoggerFn> & ErrorLogger {
    function getConsoleParams(
        message: string | Error,
        extraData: Record<string, any>
    ): [string | Error, Record<string, any>] | [string | Error] {
        if (isEmpty(extraData)) {
            return [message];
        }

        return [message, extraData];
    }

    return {
        debug(message: string, extraData: Record<string, any> = {}) {
            console.debug(...getConsoleParams(message, extraData));
            datadogLogs.logger.debug(message, { ...metadata, ...extraData, context });
        },
        log(message: string, extraData: Record<string, any> = {}) {
            console.log(...getConsoleParams(message, extraData));
            datadogLogs.logger.log(message, { ...metadata, ...extraData, context });
        },
        warn(message: string | Error, extraData: Record<string, any> = {}) {
            if (isString(message)) {
                console.warn(...getConsoleParams(message, extraData));
                datadogRum.addAction(message, { ...metadata, ...extraData, context, level: LOG_LEVELS.warn });
                datadogLogs.logger.warn(message, { ...metadata, ...extraData, context });
            } else {
                datadogRum.addAction(message.toString(), {
                    ...metadata,
                    ...extraData,
                    context,
                    level: LOG_LEVELS.warn,
                });
                datadogLogs.logger.warn(message.toString(), { ...metadata, ...extraData, context });
            }
        },
        action(message: string, extraData: Record<string, any> = {}) {
            datadogRum.addAction(message, { ...metadata, ...extraData, context });
        },
        error(msg: string | Error, extraData: Record<string, any> = {}) {
            console.error(...getConsoleParams(msg, extraData));

            if (isString(msg)) {
                const err = new Error(msg);
                // instead of overwriting the error that was passed in, we capture it and represent it in the log
                const { error: contextError } = extraData;
                if (contextError && contextError instanceof Error) {
                    err.stack = contextError.stack || "";
                } else {
                    Error.captureStackTrace(err, this.error);
                }
                datadogRum.addError(err, { ...metadata, ...extraData, context });

                // error.toString() returns 'error.name: error.message'.
                // add error to context
                datadogLogs.logger.error(err.toString(), {
                    ...metadata,
                    ...extraData,
                    ...(contextError ? { contextError: contextError.message } : {}),
                    context,
                    error: err,
                });
            } else {
                datadogRum.addError(msg, { ...metadata, ...extraData, context });
                datadogLogs.logger.error(msg.toString(), { ...metadata, ...extraData, context, error: msg });
            }
        },
    };
}

export const COPILOT_SESSION_ID_RUM_CONTEXT_KEY = "copilotSessionId";

export function addLoggerGlobalContext(key: string, value: string | undefined): void {
    datadogLogs.addLoggerGlobalContext(key, value);
}

export function setCopilotSessionRumGlobalContext(copilotSessionId: string | null | undefined): void {
    if (copilotSessionId) {
        datadogRum.addRumGlobalContext(COPILOT_SESSION_ID_RUM_CONTEXT_KEY, copilotSessionId);
        datadogLogs.addLoggerGlobalContext(COPILOT_SESSION_ID_RUM_CONTEXT_KEY, copilotSessionId);
        return;
    }

    datadogRum.removeRumGlobalContext(COPILOT_SESSION_ID_RUM_CONTEXT_KEY);
    datadogLogs.removeLoggerGlobalContext(COPILOT_SESSION_ID_RUM_CONTEXT_KEY);
}

export function normalizeError(err: typeof Error | string | unknown) {
    return err instanceof Error ? err : new Error(String(err));
}

export type ErrorMessageOpts = {
    content: string | Error; // what end user will see
    error?: Error;
    duration?: number | undefined;
    onClose?: ConfigOnClose | undefined;
    extraData?: Record<string, any>;
    loggerContext?: string;
    logger?: ErrorLogger;
    customNode?: ReactNode;
    destroyOnClick?: boolean;
    logAsWarn?: boolean;
};

export function renderErrorMessage({
    content,
    error,
    duration,
    onClose,
    extraData,
    loggerContext,
    logger,
    customNode,
    destroyOnClick = false,
    logAsWarn = false,
}: ErrorMessageOpts): void {
    logger = logger || getLogger(loggerContext || "Visual Error");
    if (logAsWarn) {
        logger.warn(error || content, { ...extraData, ...(error && content ? { visualDisplay: content } : {}) });
    } else {
        logger.error(error || content, { ...extraData, ...(error && content ? { visualDisplay: content } : {}) });
    }
    let contentToDisplay: ReactNode | string;
    if (customNode) {
        contentToDisplay = customNode;
    } else if (isString(content)) {
        contentToDisplay = content;
    } else if (content?.message) {
        contentToDisplay = content.message;
    } else {
        contentToDisplay = "There was an error";
    }
    const key = Math.random();
    message.error({
        key,
        content: contentToDisplay,
        duration,
        onClose,
        onClick: () => {
            if (destroyOnClick) {
                message.destroy(key);
            }
        },
    });
}
